"use client";

import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { SDUIUPLTComponentsPerClientSize } from "@faire/retailer-visitor-shared/components/SDUI/UPLT/calculateNumComponentsPerClientSize";
import { UPLTWrapper } from "@faire/retailer-visitor-shared/components/SDUI/UPLT/UPLTWrapper";
import { PageInitialized } from "@faire/retailer-visitor-shared/events";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { shouldShowRscProductTileLoggedInSearchPage } from "@faire/retailer-visitor-shared/settings/getSettingRscProductTileLoggedInSearchPage";
import { useSearchPageShowNoProductResults } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPageShowNoProductResults";
import { useSearchPageFiltersIsLoading } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Filters/useSearchPageFiltersIsLoading";
import { useSearchPageQueryDataUpdatedAt } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryDataUpdatedAt";
import { useSearchPageQueryIsError } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryIsError";
import { useSearchPageQueryIsLoading } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryIsLoading";
import { useSearchPageQueryQueryKey } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryKey";
import { useSearchPageIsFromDiscoverURL } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { useSearchPageProductSearchString } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { useSearchPageActionName } from "@faire/retailer-visitor-shared/views/search-v2/util/useSearchPageActionName";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { ActionName } from "@faire/web--source/common/performance/userPerceivedLoadTiming/types";
import { UserPerceivedLoadTimingContext } from "@faire/web--source/common/performance/userPerceivedLoadTiming/UserPerceivedLoadTimingContext";
import { trackRetailersPageViewView } from "@faire/web-api--source/events/retailers/view/pageView";
import { WebClientSize } from "@faire/web-api--source/indigofair/data/WebClientSize";
import productPage from "@faire/web-api--source/routes/www/product/productToken";
import React, { useContext, useEffect, use } from "react";
import { useLocation } from "react-router-dom";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

const NUM_COMPONENTS = 1;
const NUM_COMPONENTS_PER_CLIENT_SIZE: SDUIUPLTComponentsPerClientSize = {
  [WebClientSize.MOBILE]: NUM_COMPONENTS,
  [WebClientSize.TABLET]: NUM_COMPONENTS,
  [WebClientSize.DESKTOP]: NUM_COMPONENTS,
  [WebClientSize.XLARGE_DESKTOP]: NUM_COMPONENTS,
  [WebClientSize.WEB_CLIENT_SIZE_UNKNOWN]: NUM_COMPONENTS,
};

// Changes to UPLT tracking should also be made in useSearchPagePerformanceTracking.ts and LoggedInSearchPageSDUIUPLTWithContext.tsx
export const SearchPagePerformanceTrackingWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const isFromDiscoverURL = useSearchPageIsFromDiscoverURL();
  const isProductsLoading = useSearchPageQueryIsLoading();
  const isFiltersLoading = useSearchPageFiltersIsLoading();
  const isLoading = isProductsLoading || isFiltersLoading;
  const showNoProductResults = useSearchPageShowNoProductResults();
  const isError = useSearchPageQueryIsError();
  const queryKey = useSearchPageQueryQueryKey();
  const dataUpdatedAt = useSearchPageQueryDataUpdatedAt();
  const queryString = useSearchPageProductSearchString();
  const { pathname } = useLocation();

  const { prefetch: nextPrefetch } = useNextRouterContext();

  const queryKeyString = JSON.stringify(queryKey);
  const name: ActionName = useSearchPageActionName();

  const isLoggedIn = useIsLoggedInRetailer();

  useEffect(() => {
    if (!isLoading) {
      PageInitialized.publish();
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      trackRetailersPageViewView(
        "",
        "",
        queryString ?? "",
        isFromDiscoverURL ? "discover" : "search",
        pathname,
        name
      );
    }
  }, [isLoading, isFromDiscoverURL, queryString, pathname, name]);

  const handlePageIsInteractive = () => {
    nextPrefetch(productPage("productToken"));
  };

  return (
    <UPLTWrapper
      name={name}
      numComponentsPerClientSize={NUM_COMPONENTS_PER_CLIENT_SIZE}
      enableTrackPerformanceBreakdown="internal-tracking"
      shouldUseWaitForImagesToLoadPromise={isLoggedIn}
      pageJsExecutedMark={PAGE_JS_EXECUTED_MARK}
      isPageLoading={isLoading && !isError}
      dataUpdatedAt={dataUpdatedAt}
      pageLoadKey={queryKeyString}
      onImageLoad={handlePageIsInteractive}
      enabled={true}
      ignoreImages={showNoProductResults || isError}
    >
      {children}
    </UPLTWrapper>
  );
};

// Defer rendering until critical components are loaded
export const useSearchPageNonPrimaryContentDeferRender = () => {
  const shouldDeferRender = shouldShowRscProductTileLoggedInSearchPage();
  const { renderNonPrimaryContentPromise } = useContext(
    UserPerceivedLoadTimingContext
  );
  if (shouldDeferRender) {
    // use will wait for the promise to resolve before rendering the component
    // and render suspense fallback if the promise is not resolved yet
    use(renderNonPrimaryContentPromise);
  }
};

export const useSearchPagePerformanceTrackingRefCallback =
  (): React.RefCallback<HTMLDivElement> => {
    return (
      useContext(UserPerceivedLoadTimingContext)?.refCallback ??
      emptyRefCallback
    );
  };

function emptyRefCallback() {}
